import { Dialog, DialogContent } from "@/components/ui/dialog";
import checkoutAnimation from "./assets/animation-check.json";
import Lottie from "lottie-react";
import Text from "@/components/Text";
import type { StripeProduct, StripeSubscription } from "@/API";
import {
  useEffect,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router";
import type { View } from "@/pages/SubscriptionsAndPayment";
import { triggerEvent } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type Props = {
  stripeSubscription: StripeSubscription;
  listProducts: StripeProduct[];
  setView: Dispatch<SetStateAction<View>>;
};

const PaymentSucceed: FC<Props> = ({ stripeSubscription, listProducts }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const product = listProducts.find(({ prices }) =>
    prices.find((price) => price?.id === stripeSubscription.priceId),
  );

  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (product) {
      const findPrice = product.prices.findIndex(
        (price) => price && price.id === stripeSubscription.priceId,
      );
      if (findPrice != -1) {
        const price = product.prices[findPrice];

        if (price) {
          triggerEvent("Subscription Started", {
            plan_name: product.name,
            billing_period: price.interval,
          });
        }
      }
    }
  }, [product]);

  if (!product) return null;

  const { name } = product;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        triggerEvent("Upgrade Success CTA Clicked", {
          plan: product.name,
          action: "close_modal",
        });
      }}
    >
      <DialogContent className="p-[40px]">
        <div className="flex flex-col items-center">
          <Lottie
            className="h-[80px] w-[80px]"
            animationData={checkoutAnimation}
            loop={true}
          />
          <div className="flex flex-col gap-[8px]">
            <Text fontWeight={600} fontSize={24} className="text-center">
              {t("payment_succeed_title", { productName: name })}
            </Text>
            <Text fontWeight={400} fontSize={14} className="text-center">
              {t("payment_succeed_subtitle")}
            </Text>
          </div>

          <div className="flex flex-col gap-[8px] my-[32px] w-full">
            <div className="bg-[#F7F8F9] py-[8px] px-[16px] rounded-[12px] w-[fit-content] flex items-center gap-[12px]">
              <img src="/img/emojis/stats.png" height={20} width={20} />
              <Text fontSize={14} fontWeight={400}>
                {t("payment_succeed_description_1")}
              </Text>
            </div>
            <div className="bg-[#F7F8F9] py-[8px] px-[16px] rounded-[12px] w-[fit-content] flex items-center gap-[12px]">
              <img src="/img/emojis/pin.png" height={20} width={20} />
              <Text fontSize={14} fontWeight={400}>
                {t("payment_succeed_description_2")}
              </Text>
            </div>
            <div className="bg-[#F7F8F9] py-[8px] px-[16px] rounded-[12px] w-[fit-content] flex items-center gap-[12px]">
              <img src="/img/emojis/rocket.png" height={20} width={20} />
              <Text fontSize={14} fontWeight={400}>
                {t("payment_succeed_description_3")}
              </Text>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
            <Button
              onClick={() => {
                triggerEvent("Upgrade Success CTA Clicked", {
                  plan: product.name,
                  action: "go_to_explorer",
                });
                navigate("/explorer");
              }}
              variant="secondaryFilled"
            >
              {t("payment_succeed_action_1")}
            </Button>
            <Button
              onClick={() => {
                triggerEvent("Upgrade Success CTA Clicked", {
                  plan: product.name,
                  action: "go_to_add_restaurant",
                });
                navigate("/find-restaurant");
                setOpen(false);
              }}
            >
              {t("payment_succeed_action_2")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentSucceed;
